import React, { useState, useEffect, useRef, useCallback } from "react";
import { getPlaceAutocomplete } from "../services/locationService";
import { motion, AnimatePresence } from "framer-motion";
import { MapPinIcon } from "./icons/FeatherIcons";
import { useToast } from "../contexts/ToastContext";

interface LocationAutocompleteProps {
  initialValue: string;
  onLocationSelect: (location: {
    description: string;
    placeId: string;
  }) => void;
  onInputChange?: (value: string) => void;
  label?: string;
  id?: string;
  onFocus?: (inputRef: React.RefObject<HTMLInputElement>) => void;
  onBlur?: () => void;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  initialValue,
  onLocationSelect,
  onInputChange,
  label,
  id,
  onFocus,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(initialValue);
    if (onFocus) onFocus(inputRef);
  }, [initialValue]);

  const fetchSuggestions = useCallback(
    async (value: string) => {
      if (value.length < 3) {
        setSuggestions([]);
        return;
      }
      try {
        const results = await getPlaceAutocomplete(value);
        setSuggestions(results);
      } catch (error) {
        if (error instanceof Error) {
          addToast(error.message, "error");
        }
        setSuggestions([]);
      }
    },
    [addToast],
  );

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue && isFocused) {
        fetchSuggestions(inputValue);
      }
    }, 300); // 300ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, isFocused, fetchSuggestions]);

  // Handle clicks outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (suggestion: any) => {
    const newInputValue = suggestion.description;
    setInputValue(newInputValue);
    if (onInputChange) {
      onInputChange(newInputValue);
    }
    onLocationSelect({
      description: newInputValue,
      placeId: suggestion.place_id,
    });
    setSuggestions([]);
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-600 mb-1"
        >
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onFocus={() => {
          setIsFocused(true);
          if (onFocus) onFocus(inputRef);
        }}
        onBlur={() => {
          // Delay the blur handler to allow onClick to fire first
          setTimeout(() => {
            setIsFocused(false);
            setSuggestions([]);
            if (onBlur) onBlur();
          }, 200);
        }}
        placeholder="e.g., Marina Bay Sands"
        className="w-full bg-gray-100 rounded-lg px-4 py-2.5 ring-2 ring-transparent focus:ring-primary focus:outline-none text-gray-900 border border-gray-300"
        autoComplete="off"
      />
      <AnimatePresence>
        {isFocused && suggestions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.place_id}
                onClick={() => handleSelect(suggestion)}
                className="px-4 py-3 cursor-pointer hover:bg-gray-100 flex items-center"
              >
                <MapPinIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {suggestion.structured_formatting.main_text}
                  </p>
                  <p className="text-xs text-gray-500">
                    {suggestion.structured_formatting.secondary_text}
                  </p>
                </div>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LocationAutocomplete;
